import React from 'react'
import capitalize from 'lodash/capitalize'
import { FieldError } from 'react-hook-form'
import { Chip } from '@common/material'
import { Box, Text, Flex } from '@fundamentals'
import {
  TaskTemplatePriorityType,
  TaskTemplateProperties,
} from '@core/entities/task/TaskTemplateProperties'
import { Palette } from '@core/theme/color-palette-siteassist'
import { localizedStrings } from '@core/strings'

interface TaskPriorityPickerProps {
  priorityOptions: TaskTemplatePriorityType[]
  value: TaskTemplatePriorityType
  onChange: (priority: TaskTemplatePriorityType) => void
  error?: FieldError
  hideLabel?: boolean
  label?: string
}

export const TaskPriorityPicker: React.FC<TaskPriorityPickerProps> = ({
  value,
  onChange,
  priorityOptions,
  error,
  hideLabel,
  label,
}) => {
  return (
    <Box>
      {!hideLabel && (
        <Flex color='grey.600' alignItems='center'>
          <Text fontSize='14px' mb={1}>
            {label || localizedStrings.priority}
          </Text>
        </Flex>
      )}

      <Flex gap={1}>
        {priorityOptions.map((priority) => {
          const isSelected = priority === value

          return (
            <Chip
              data-test={`task-priority-picker-option-${priority}`}
              key={priority}
              onClick={() => onChange(priority)}
              variant='outlined'
              sx={{
                height: 36,
                minWidth: 86,
                borderRadius: 1,
                px: 1,
                '& .MuiChip-label': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  pl: 0.5,
                  pr: 0.5,
                },
                backgroundColor: isSelected
                  ? TaskTemplateProperties.getColorForPriority(priority)
                  : Palette.Grey[100],
                borderColor: isSelected
                  ? TaskTemplateProperties.getColorForPriorityBorder(priority)
                  : Palette.Grey[200],
                color: 'text.primary',
                '&&.MuiChip-clickable.MuiChip-outlined:hover': {
                  backgroundColor: isSelected
                    ? TaskTemplateProperties.getColorForPriority(priority)
                    : Palette.Grey[200],
                },
              }}
              label={
                <Text
                  variant='body1'
                  color={
                    isSelected &&
                    TaskTemplateProperties.getIsHighContrastColorPriority(
                      priority,
                    )
                      ? Palette.Common.White
                      : Palette.Text.primary
                  }
                >
                  {capitalize(priority)}
                </Text>
              }
            />
          )
        })}
      </Flex>

      {error && (
        <Text color='error' fontSize='body2' mt={1}>
          {error.message}
        </Text>
      )}
    </Box>
  )
}
